import React, { useState, useRef } from 'react';
import api from '../api';
import PageHeader from './PageHeader';

export default function TextMemory({ onSuccess, onBack }) {
  const [guestName, setGuestName] = useState('');
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const canvasRef = useRef(null);
  const isDrawingRef = useRef(false);
  const lastPosRef = useRef({ x: 0, y: 0 });

  // ─── Canvas İmza İşlevleri ───
  const getPos = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    return {
      x: (clientX - rect.left) * (canvas.width / rect.width),
      y: (clientY - rect.top) * (canvas.height / rect.height)
    };
  };

  const startDraw = (e) => {
    e.preventDefault();
    isDrawingRef.current = true;
    lastPosRef.current = getPos(e);
  };

  const draw = (e) => {
    e.preventDefault();
    if (!isDrawingRef.current) return;
    const ctx = canvasRef.current.getContext('2d');
    const pos = getPos(e);
    ctx.beginPath();
    ctx.moveTo(lastPosRef.current.x, lastPosRef.current.y);
    ctx.lineTo(pos.x, pos.y);
    ctx.strokeStyle = '#4a5540';
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.stroke();
    lastPosRef.current = pos;
  };

  const endDraw = () => {
    isDrawingRef.current = false;
  };

  const clearSignature = () => {
    const ctx = canvasRef.current.getContext('2d');
    ctx.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
  };

  const handleSubmit = async () => {
    if (!guestName.trim() || !message.trim()) return;
    setSubmitting(true);
    try {
      const signatureData = canvasRef.current.toDataURL('image/png');
      await api.post('/api/memories/text', {
        guestName: guestName.trim(),
        message: message.trim(),
        signatureData,
        screenSize: window.screen.width + 'x' + window.screen.height,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        platform: navigator.platform
      });
      onSuccess('Mesajınız ve imzanız kaydedildi! Teşekkür ederiz ✍️');
      setGuestName('');
      setMessage('');
      clearSignature();
    } catch {
      alert('Bir hata oluştu, lütfen tekrar deneyin.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="flex flex-col items-center px-6 pt-14 pb-8 max-w-md mx-auto">
      <PageHeader title="Yazılı Anı & İmza" onBack={onBack} />

      {/* Başlık */}
      <div className="text-center mb-8 mt-4">
        <h2 className="font-playfair text-2xl text-sage-800 font-semibold">Yazılı Anı & İmza</h2>
        <p className="text-sage-500 text-sm mt-2">Güzel dileklerinizi bırakın</p>
      </div>

      {/* Form */}
      <div className="w-full space-y-5">
        {/* İsim */}
        <div>
          <label className="block text-sage-600 text-sm font-medium mb-2">Adınız</label>
          <input
            type="text"
            value={guestName}
            onChange={(e) => setGuestName(e.target.value)}
            placeholder="Adınız ve soyadınız"
            maxLength={100}
            className="w-full px-4 py-3 bg-white/70 backdrop-blur-sm border border-sage-200 rounded-xl
                       text-sage-800 placeholder:text-sage-300 focus:outline-none focus:border-gold-400
                       focus:ring-2 focus:ring-gold-300/20 transition-all"
          />
        </div>

        {/* Mesaj */}
        <div>
          <label className="block text-sage-600 text-sm font-medium mb-2">Mesajınız</label>
          <textarea
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder="Çiftimize güzel dileklerinizi yazın..."
            maxLength={2000}
            rows={5}
            className="w-full px-4 py-3 bg-white/70 backdrop-blur-sm border border-sage-200 rounded-xl
                       text-sage-800 placeholder:text-sage-300 focus:outline-none focus:border-gold-400
                       focus:ring-2 focus:ring-gold-300/20 transition-all resize-none"
          />
          <p className="text-right text-sage-400 text-xs mt-1">{message.length}/2000</p>
        </div>

        {/* İmza Alanı */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="text-sage-600 text-sm font-medium">İmzanız</label>
            <button
              onClick={clearSignature}
              className="text-sage-400 text-xs hover:text-sage-600 transition-colors"
            >
              Temizle
            </button>
          </div>
          <div className="bg-white/80 border-2 border-dashed border-sage-200 rounded-xl overflow-hidden">
            <canvas
              ref={canvasRef}
              width={600}
              height={200}
              className="w-full h-32 signature-canvas"
              onMouseDown={startDraw}
              onMouseMove={draw}
              onMouseUp={endDraw}
              onMouseLeave={endDraw}
              onTouchStart={startDraw}
              onTouchMove={draw}
              onTouchEnd={endDraw}
            />
          </div>
          <p className="text-sage-400 text-xs mt-1 text-center">Parmağınızla veya fare ile imzanızı çizin</p>
        </div>

        {/* Gönder Butonu */}
        <button
          onClick={handleSubmit}
          disabled={!guestName.trim() || !message.trim() || submitting}
          className="w-full py-3.5 bg-sage-600 text-white rounded-xl font-medium
                     hover:bg-sage-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed
                     active:scale-[0.98]"
        >
          {submitting ? (
            <span className="flex items-center justify-center gap-2">
              <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" /></svg>
              Gönderiliyor...
            </span>
          ) : 'Anımı Gönder'}
        </button>
      </div>
    </div>
  );
}
